// @givmo/app-charity — the charity class's console table (CPA-WEB40 · spec §5 · shell contract v3).
//
// ══ THE CLASS TABLE IS DATA, AND THE CONSOLE IS BUILT FROM IT ═══════════════════════════════════
//
// `@givmo/org-console` takes NO registry, router or environment: a host-supplied `ConsoleTableRow[]`
// (`{slug, path?, href?, classDefault?, adminConsole?}`) plus the standing read, the pathname and
// the origin is the whole input to its mechanics — the ONE square derivation (`deriveConsoleSquares`),
// the ONE landing rule (`landingDestination`), the ONE sidebar rule (`sidebarFor`), which module a
// URL is inside, and whether a `$module/sign-in` door or a `$module/team` page exists. This table is
// that input for the charity class. Nothing here composes a rule of its own.
//
// ⚠ MOUNTED ≡ PATH AND A CONFIGURED HREF, AND THE HREF IS COMPUTED AT CALL TIME. One bundle serves
// every environment. A mounted module's href is `new URL(path, origin)` resolved when asked — never
// a literal host, never a per-environment map, never a fallback. `module-table.test.ts` carries the
// no-defaults rail over this file's source. A row with no href is dropped by the derivation: a
// square is real or absent.
//
// ⚠ DONATIONS IS AN ORDINARY CATALOG MODULE, AND THE CLASS DEFAULT (D-CP-42/43/44, §9-A A1). The
// backend's charity catalog carries `donations` (CPA-BE28): on for every claimed charity, never
// switchable, held per person like every other module. Its tile state comes from the self read's
// `modules[]` row through the shared verdict exactly like `compliance` — no host special case in the
// derivation. The ONE Donations-specific rule this host keeps is the §9-14 fall-through in
// `charity-console-context.tsx` (a REPORTED map lacking the row is a deploy-ordering artifact).
//
// ⚠ HOME IS NOT A THING (R2/A2). No `console` row, no suite-home square, no `menu-home`. What sits
// LAST is the ADMIN CONSOLE row — the organization's own management pages (`/org`) as a tile that
// renders only for a holder of one of its pages' capabilities (never a tier check), badged with the
// pending-request count. It is not a backend module: the catalog reports no standing for it.
//
// Rows, in popup order:
//   donations     /donations           mounted always — the class program's own surface; CLASS DEFAULT
//   compliance    /compliance          label `Compliance` (the tile's short word; its pages are Calendar · Filings · Documents, the Calendar at the root); mounted while `VITE_COMPLIANCE_UI_ENABLED` is on (CCM-MOD1B: the `/filing-register*` addresses replace-redirect here)
//   books         /books               mounted while `VITE_BOOKS_MODULE_ENABLED` is on
//   fundraisers   /fundraisers         mounted while `VITE_FUNDRAISERS_MODULE_ENABLED` is on
//   donors        /crm                 label `CRM` (the catalog slug is `donors`); mounted while `VITE_CRM_MODULE_ENABLED` is on
//   org           /org                 the ADMIN CONSOLE — last; `adminConsole: true`
//   board         /board               mounted while VITE_BOARD_UI_ENABLED is on
//   connect       —                    OMITTED: grant-by-exception, no page of its own

import {
  Boxes,
  Building2,
  Gavel,
  HandCoins,
  Megaphone,
  ScrollText,
  Users,
  type LucideProps,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { COMPLIANCE_ROUTES } from '@givmo/compliance';
import { BOOKS_ROUTE } from '@givmo/books';
import { FUNDRAISERS_ROUTES } from '@givmo/fundraisers-module';
import { isMountedInThisBuild, type ConsoleTableRow, type ModuleRoute } from '@givmo/org-console';
import {
  isBooksModuleEnabled,
  isComplianceUiEnabled,
  isCrmModuleEnabled,
  isFundraisersModuleEnabled,
  isBoardUiEnabled,
} from '../lib/env';
import { DONATIONS_ROUTE, ORG_ROUTE } from './console-routes';

export type CharityModuleIcon = ComponentType<LucideProps>;

/** The class default's slug — exactly one row carries the flag, pinned by `module-table.test.ts`. */
export const CLASS_DEFAULT_SLUG = 'donations';

/** The Admin Console row's slug — a square needs one (`module-square-org`); it is not a module. */
export const ADMIN_CONSOLE_SLUG = 'org';

/**
 * Modules with no page of their own, listed wherever the backend's catalog carries them (OM-95):
 * Connected AI, whose act — connecting an AI to the organization — happens in the AI's own client.
 */
export const PAGELESS_MODULE_SLUGS: readonly string[] = ['connect'];

/** The Admin Console's label — the tile's word, the sidebar section's caption, the account-menu row. */
export const ADMIN_CONSOLE_LABEL = 'Admin Console';

/** The Member-catalog read string CPA-BE28 mints for the class's own module — the module rows' `requires`. */
export const DONATIONS_READ_PERMISSION = 'donations:read';

export interface CharityModuleDefinition {
  /** The catalog slug (`donors`, not `crm`) — the key the org self read reports standings under. */
  slug: string;
  /** The path this row lives at inside the console, mounted or not. */
  path: string;
  /** The word people see. */
  label: string;
  /** The tile's second line, when the row earns one. */
  description?: string;
  icon: CharityModuleIcon;
  /** Whether the row is mounted in THIS build — read at call time, never at module load. */
  mounted: () => boolean;
  /** The class's DEFAULT LANDING module — exactly ONE (T1.4). */
  classDefault?: true;
  /** The organization's own management tile — at most ONE (T4.1). Not a module. */
  adminConsole?: true;
}

export const CHARITY_MODULE_TABLE: readonly CharityModuleDefinition[] = [
  {
    slug: CLASS_DEFAULT_SLUG,
    path: DONATIONS_ROUTE,
    label: 'Donations',
    description: 'Your statement and payment setup',
    icon: HandCoins,
    mounted: () => true,
    classDefault: true,
  },
  // The TILE's word is the registry's short label (the walkthrough decision, 2026-09-03), and the
  // console vocab's too — the Apps card, Team and access panels and the Notifications card say
  // "Compliance" (CCM-R1-REMIND-WEB); the module's pages are its own rows (`console-nav.ts`).
  {
    slug: 'compliance',
    path: COMPLIANCE_ROUTES.calendar,
    label: 'Compliance',
    description: 'State filings and what was recorded',
    icon: ScrollText,
    mounted: isComplianceUiEnabled,
  },
  {
    slug: 'books',
    path: BOOKS_ROUTE,
    label: 'Books',
    icon: Boxes,
    mounted: isBooksModuleEnabled,
  },
  {
    slug: 'fundraisers',
    path: FUNDRAISERS_ROUTES.list,
    label: 'Fundraisers',
    icon: Megaphone,
    mounted: isFundraisersModuleEnabled,
  },
  {
    slug: 'donors',
    path: '/crm',
    label: 'CRM',
    icon: Users,
    mounted: isCrmModuleEnabled,
  },
  // The company registry's glyph for the same module, so one module reads the same in both consoles.
  { slug: 'board', path: '/board', label: 'Board', icon: Gavel, mounted: isBoardUiEnabled },
  // The organization's own management pages as a tile — see the header and `adminConsole`.
  {
    slug: ADMIN_CONSOLE_SLUG,
    path: ORG_ROUTE,
    label: ADMIN_CONSOLE_LABEL,
    description: 'Apps, team, activity',
    icon: Building2,
    mounted: () => true,
    adminConsole: true,
  },
];

/**
 * One class-table row as the shared derivation reads it: a {@link ConsoleTableRow} (slug · path ·
 * the resolved destination · the two v3 flags) that keeps the display fields, so a host maps a
 * square straight to its tile without a second lookup.
 */
export interface CharityTableRow extends ConsoleTableRow {
  path: string;
  label: string;
  description?: string;
  icon: CharityModuleIcon;
}

/** The live origin — the ONE site in this app that reads it (the one-resolver rail). */
export function liveOrigin(): string {
  return window.location.origin;
}

/** A row by slug — the Admin Console row included. */
export function charityModuleDefinition(slug: string): CharityModuleDefinition | undefined {
  return CHARITY_MODULE_TABLE.find((definition) => definition.slug === slug);
}

/**
 * The ONE console table — every row with its destination resolved from `origin` NOW (the row is
 * mounted in this build) or left without one (published-unmounted). The squares, the landing and
 * the sidebar derive from exactly this list.
 */
export function charityConsoleTable(origin: string = liveOrigin()): CharityTableRow[] {
  return CHARITY_MODULE_TABLE.map(({ mounted, ...definition }) => ({
    ...definition,
    ...(mounted() ? { href: new URL(definition.path, origin).href } : {}),
  }));
}

/**
 * The MODULES this build serves at a path of their own — the substrate's input (which module a URL
 * is inside, the `$module/sign-in` door, the `$module/team` page, the direct-link gate).
 *
 * ⚠ THE ADMIN CONSOLE ROW IS NOT A MODULE and is never in this list: its `/org` pages are the
 * console's own, declared by the shell (`features/org/org-pages.ts`), and a sign-in door or a Team
 * page for them would be a door onto the organization's management pages wearing a product's name.
 */
export function charityModuleRoutes(origin: string = liveOrigin()): ModuleRoute[] {
  return charityConsoleTable(origin).filter(
    (row) => row.adminConsole !== true && isMountedInThisBuild(row),
  );
}
